/* ============================================================================
   Atmósfera estándar (ISA) y altitud densidad.

   Esto es lo que el prototipo no tenía y el pliego pide explícitamente: la
   potencia disponible de un helicóptero no depende de la altitud sobre el mar
   sino de la DENSIDAD del aire, que cae con la altitud y sube con el frío. Un
   día caluroso en Quito o en Cusco puede poner a la máquina, en términos de
   performance, miles de pies "más arriba" de donde está parada.

   Modelo: atmósfera estándar internacional (ISA) hasta la tropopausa.
   No sustituye a las cartas del RFM — sirve para situar el punto de entrada
   a esas cartas y para explicar por qué el margen cambia.
   ========================================================================== */

/** Temperatura ISA al nivel del mar, °C. */
export const ISA_TEMP_SL_C = 15;
/** Presión ISA al nivel del mar, hPa. */
export const ISA_PRESION_SL_HPA = 1013.25;
/** Gradiente térmico estándar: 1,98 °C cada 1000 ft (≈ 6,5 °C/km). */
export const GRADIENTE_C_POR_1000FT = 1.98;
/** Altitud de la tropopausa en ISA, ft. Por encima, la temperatura se estabiliza. */
export const TROPOPAUSA_FT = 36089;
/** Temperatura ISA en la tropopausa y por encima, °C. */
export const ISA_TEMP_TROPOPAUSA_C = -56.5;

/**
 * Temperatura estándar ISA a una altitud dada.
 * @param {number} altitudFt altitud, en pies
 * @returns {number} temperatura, en °C
 */
export function temperaturaIsa(altitudFt) {
  if (!Number.isFinite(altitudFt)) return NaN;
  if (altitudFt >= TROPOPAUSA_FT) return ISA_TEMP_TROPOPAUSA_C;
  return ISA_TEMP_SL_C - (altitudFt / 1000) * GRADIENTE_C_POR_1000FT;
}

/**
 * Desviación respecto de ISA. Positiva = más caliente que el estándar, que es
 * el caso que degrada la performance.
 * @param {number} altitudFt
 * @param {number} oatC temperatura exterior real, en °C
 * @returns {number} ISA ± n, en °C
 */
export function desviacionIsa(altitudFt, oatC) {
  return oatC - temperaturaIsa(altitudFt);
}

/**
 * Altitud presión a partir de la elevación real y el reglaje altimétrico.
 *
 * Aproximación de campo, válida cerca del nivel del mar y en el rango normal
 * de QNH: 1 hPa ≈ 27 ft.
 *
 * @param {number} elevacionFt elevación del punto, en pies
 * @param {number} qnhHpa reglaje altimétrico, en hPa (por defecto, ISA)
 * @returns {number} altitud presión, en pies
 */
export function altitudPresion(elevacionFt, qnhHpa = ISA_PRESION_SL_HPA) {
  if (!Number.isFinite(elevacionFt)) return NaN;
  const qnh = Number.isFinite(qnhHpa) ? qnhHpa : ISA_PRESION_SL_HPA;
  return elevacionFt + (ISA_PRESION_SL_HPA - qnh) * 27;
}

/**
 * Altitud densidad.
 *
 * Fórmula de campo estándar: por cada grado por encima de ISA, la altitud
 * densidad sube unos 118,8 ft.
 *
 * @param {number} altitudPresionFt
 * @param {number} oatC temperatura exterior real, en °C
 * @returns {number} altitud densidad, en pies
 */
export function altitudDensidad(altitudPresionFt, oatC) {
  if (!Number.isFinite(altitudPresionFt) || !Number.isFinite(oatC)) return NaN;
  return altitudPresionFt + 118.8 * desviacionIsa(altitudPresionFt, oatC);
}

/**
 * Relación de densidad del aire (sigma) respecto del nivel del mar en ISA.
 * Sirve para estimar la caída de potencia disponible.
 *
 * @param {number} altitudPresionFt
 * @param {number} oatC
 * @returns {number} sigma, adimensional (1,0 = nivel del mar en ISA)
 */
export function relacionDensidad(altitudPresionFt, oatC) {
  if (!Number.isFinite(altitudPresionFt) || !Number.isFinite(oatC)) return NaN;
  // delta: relación de presión, exponente de la atmósfera estándar.
  const delta = Math.pow(1 - 6.87535e-6 * altitudPresionFt, 5.2558797);
  // theta: relación de temperatura absoluta.
  const theta = (oatC + 273.15) / (ISA_TEMP_SL_C + 273.15);
  return delta / theta;
}

/**
 * Resumen atmosférico completo de un punto.
 *
 * Devuelve todo junto porque en la práctica nunca se necesita uno solo de
 * estos valores: la decisión operativa se toma mirando el conjunto.
 */
export function resumenAtmosferico({ elevacionFt, oatC, qnhHpa = ISA_PRESION_SL_HPA }) {
  const presionFt = altitudPresion(elevacionFt, qnhHpa);
  const densidadFt = altitudDensidad(presionFt, oatC);
  return {
    elevacionFt,
    oatC,
    qnhHpa,
    altitudPresionFt: presionFt,
    altitudDensidadFt: densidadFt,
    temperaturaIsaC: temperaturaIsa(presionFt),
    desviacionIsaC: desviacionIsa(presionFt, oatC),
    sigma: relacionDensidad(presionFt, oatC),
    // Señal para la UI: la altitud densidad supera la presión en más de 2000 ft.
    // No es un límite normativo, es un umbral de atención.
    diaCaluroso: densidadFt - presionFt > 2000,
  };
}

/* ==========================================================================
   PRONÓSTICO EN ALTURA Y VIENTO

   La atmósfera estándar de arriba es un MODELO: sirve para saber cuánto se
   aparta el día real de lo normal, no para reemplazar el pronóstico.

   Open-Meteo publica temperatura y viento por niveles de presión, y ahí hay
   una trampa: la altitud de un nivel NO es fija. El de 500 hPa puede estar
   entre 5 400 y 5 800 m según el sistema de presión del día. Por eso se pide
   también la altura geopotencial y se elige el nivel por su altura REAL.
   Suponer la nominal puede errar 400 m, que en temperatura son casi 3 °C.
   ========================================================================== */

/** Niveles que publica Open-Meteo, con su altura aproximada en pies. */
export const NIVELES_PRESION = [
  { hPa: 1000, ftAprox: 360 }, { hPa: 975, ftAprox: 1000 }, { hPa: 950, ftAprox: 1640 },
  { hPa: 925, ftAprox: 2500 }, { hPa: 900, ftAprox: 3240 }, { hPa: 850, ftAprox: 4780 },
  { hPa: 800, ftAprox: 6400 }, { hPa: 700, ftAprox: 9880 }, { hPa: 600, ftAprox: 13800 },
  { hPa: 500, ftAprox: 18280 }, { hPa: 400, ftAprox: 23600 }, { hPa: 300, ftAprox: 30070 },
];

/**
 * Temperatura estimada en altura a partir de la de superficie.
 *
 * Es una ESTIMACIÓN por gradiente estándar, no un pronóstico. Sirve solo
 * cuando no hay datos en altura, y quien la use tiene que saber que la
 * atmósfera real rara vez sigue el gradiente estándar: una inversión térmica
 * lo invierte por completo.
 */
export function temperaturaEstimadaEnAltura(tempSuperficieC, altitudSuperficieFt, altitudDestinoFt) {
  if (![tempSuperficieC, altitudSuperficieFt, altitudDestinoFt].every(Number.isFinite)) return null;
  const desnivel = altitudDestinoFt - altitudSuperficieFt;
  return Math.round((tempSuperficieC - (desnivel / 1000) * GRADIENTE_C_POR_1000FT) * 10) / 10;
}

/** Los dos niveles que encierran una altitud, según su altura REAL. */
export function nivelesQueEncierran(niveles, altitudFt) {
  const validos = (niveles || [])
    .filter((n) => Number.isFinite(n?.alturaFt))
    .sort((a, b) => a.alturaFt - b.alturaFt);
  if (validos.length === 0 || !Number.isFinite(altitudFt)) return { abajo: null, arriba: null };
  if (altitudFt <= validos[0].alturaFt) return { abajo: validos[0], arriba: validos[0] };
  const ultimo = validos[validos.length - 1];
  if (altitudFt >= ultimo.alturaFt) return { abajo: ultimo, arriba: ultimo };
  for (let i = 0; i < validos.length - 1; i += 1) {
    if (altitudFt >= validos[i].alturaFt && altitudFt <= validos[i + 1].alturaFt) {
      return { abajo: validos[i], arriba: validos[i + 1] };
    }
  }
  return { abajo: ultimo, arriba: ultimo };
}

/** Interpola un campo escalar entre dos niveles, por altura real. */
export function interpolarEnAltura(niveles, altitudFt, campo) {
  const { abajo, arriba } = nivelesQueEncierran(niveles, altitudFt);
  if (!abajo || !arriba) return null;
  const a = abajo[campo];
  const b = arriba[campo];
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (abajo === arriba || abajo.alturaFt === arriba.alturaFt) return a;
  const t = (altitudFt - abajo.alturaFt) / (arriba.alturaFt - abajo.alturaFt);
  return a + (b - a) * t;
}

/**
 * Interpola la DIRECCIÓN del viento, que no se puede promediar como un número.
 *
 * El promedio de 350° y 010° no es 180°: es 000°. Interpolar direcciones como
 * escalares produce vientos que apuntan justo al revés cuando cruzan el norte.
 * Se interpola sobre las componentes del vector.
 */
export function interpolarDireccion(niveles, altitudFt) {
  const { abajo, arriba } = nivelesQueEncierran(niveles, altitudFt);
  if (!abajo || !arriba) return null;
  if (!Number.isFinite(abajo.direccion) || !Number.isFinite(arriba.direccion)) return null;
  if (abajo === arriba || abajo.alturaFt === arriba.alturaFt) return abajo.direccion;
  const t = (altitudFt - abajo.alturaFt) / (arriba.alturaFt - abajo.alturaFt);
  const rad = (g) => (g * Math.PI) / 180;
  const x = Math.cos(rad(abajo.direccion)) * (1 - t) + Math.cos(rad(arriba.direccion)) * t;
  const y = Math.sin(rad(abajo.direccion)) * (1 - t) + Math.sin(rad(arriba.direccion)) * t;
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

/**
 * Componentes del viento respecto del rumbo.
 *
 * `frontal` positivo es viento EN CONTRA. `cruzado` positivo viene de la
 * derecha. La dirección del viento se informa siempre DE DÓNDE VIENE.
 */
export function componentesDeViento(rumboVerdadero, direccionViento, velocidadKt) {
  if (![rumboVerdadero, direccionViento, velocidadKt].every(Number.isFinite)) {
    return { frontal: null, cruzado: null, valido: false };
  }
  const ang = ((direccionViento - rumboVerdadero) * Math.PI) / 180;
  return {
    valido: true,
    frontal: Math.round(velocidadKt * Math.cos(ang) * 10) / 10,
    cruzado: Math.round(velocidadKt * Math.sin(ang) * 10) / 10,
  };
}

/**
 * Velocidad respecto al suelo con viento.
 *
 * No es una resta. Con viento cruzado hay que apuntar contra la deriva, y esa
 * corrección también consume velocidad: se resuelve con el triángulo de
 * velocidades. Si el viento supera a la aeronave no hay solución, y se
 * devuelve null en vez de un número imaginario.
 */
export function velocidadSobreSuelo(tasKt, rumboVerdadero, direccionViento, velocidadVientoKt) {
  if (![tasKt, rumboVerdadero, direccionViento, velocidadVientoKt].every(Number.isFinite)) return null;
  if (tasKt <= 0) return null;
  if (velocidadVientoKt === 0) return tasKt;

  const rad = (g) => (g * Math.PI) / 180;
  const anguloViento = rad(direccionViento + 180 - rumboVerdadero);
  const senoDeriva = (velocidadVientoKt * Math.sin(anguloViento)) / tasKt;
  if (Math.abs(senoDeriva) > 1) return null;
  const deriva = Math.asin(senoDeriva);
  const gs = tasKt * Math.cos(deriva) + velocidadVientoKt * Math.cos(anguloViento);
  return gs > 0 ? Math.round(gs * 10) / 10 : null;
}

/**
 * Velocidad de crucero máxima del AS350 B3, en nudos indicados.
 *
 * Es un límite de la aeronave, no una preferencia: por encima no se vuela.
 * Escribir 200 en el campo no lo convierte en posible, así que la app lo acota
 * y avisa en vez de calcular una ruta que no existe.
 */
export const VELOCIDAD_CRUCERO_MAXIMA_KT = 165;

/**
 * Acota la velocidad de crucero pedida al máximo de la aeronave.
 *
 * Devuelve también si hubo que recortarla, para poder decirlo: recortar en
 * silencio haría que el piloto viera un tiempo de vuelo que no corresponde al
 * número que escribió.
 */
export function acotarVelocidadCrucero(kt, maximoKt = VELOCIDAD_CRUCERO_MAXIMA_KT) {
  if (!Number.isFinite(kt) || kt <= 0) {
    return { kt: null, recortada: false, motivo: "Sin velocidad de crucero no se puede estimar el tiempo de vuelo." };
  }
  if (kt > maximoKt) {
    return {
      kt: maximoKt,
      recortada: true,
      motivo: `${Math.round(kt)} kt supera el máximo de ${maximoKt} kt de la aeronave. Se calcula con ${maximoKt} kt.`,
    };
  }
  return { kt, recortada: false, motivo: null };
}
